import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, ArrowRightLeft, Check, X, Trash2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { TradeDetailsModal } from './TradeDetailsModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const TradesTab = ({ leagueId, token, userId, professors, teams, onTradeComplete }) => {
  const [trades, setTrades] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showProposeDialog, setShowProposeDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTrade, setSelectedTrade] = useState(null);
  const [showTradeModal, setShowTradeModal] = useState(false);
  
  // Form states for proposing trade
  const [selectedMyProfessor, setSelectedMyProfessor] = useState('');
  const [selectedTargetUser, setSelectedTargetUser] = useState('');
  const [selectedTargetProfessor, setSelectedTargetProfessor] = useState('');

  // Get my team
  const myTeam = teams.find(t => t.owner_id === userId || t.partner_id === userId);
  
  // Get my assigned professors
  const myProfessors = professors.filter(p => p.assigned_to === myTeam?.id);
  
  // Get other teams
  const otherTeams = teams.filter(t => t.id !== myTeam?.id);

  const fetchTrades = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/api/trades/${leagueId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setTrades(data);
      }
    } catch (err) {
      console.error('Error fetching trades:', err);
    } finally {
      setIsLoading(false);
    }
  }, [leagueId, token]);

  useEffect(() => {
    fetchTrades();
  }, [fetchTrades]);

  const handleProposeTrade = async () => {
    if (!selectedMyProfessor || !selectedTargetUser || !selectedTargetProfessor) {
      toast.error('Seleziona tutti i campi');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/trades`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          league_id: leagueId,
          from_professor_id: selectedMyProfessor,
          to_user_id: selectedTargetUser,
          to_professor_id: selectedTargetProfessor,
        }),
      });

      if (response.ok) {
        toast.success('Proposta di scambio inviata!');
        setShowProposeDialog(false);
        setSelectedMyProfessor('');
        setSelectedTargetUser('');
        setSelectedTargetProfessor('');
        fetchTrades();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nella proposta di scambio');
      }
    } catch (err) {
      toast.error('Errore di rete');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAcceptTrade = async (tradeId) => {
    try {
      const response = await fetch(`${API_URL}/api/trades/${tradeId}/accept`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success('Scambio accettato! I professori sono stati scambiati.');
        fetchTrades();
        if (onTradeComplete) onTradeComplete();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nell\'accettare lo scambio');
      }
    } catch (err) {
      toast.error('Errore di rete');
    }
  };

  const handleRejectTrade = async (tradeId) => {
    try {
      const response = await fetch(`${API_URL}/api/trades/${tradeId}/reject`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success('Scambio rifiutato');
        fetchTrades();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel rifiutare lo scambio');
      }
    } catch (err) {
      toast.error('Errore di rete');
    }
  };

  const handleCancelTrade = async (tradeId) => {
    try {
      const response = await fetch(`${API_URL}/api/trades/${tradeId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        toast.success('Proposta cancellata');
        fetchTrades();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nell\'eliminare la proposta');
      }
    } catch (err) {
      toast.error('Errore di rete');
    }
  };

  // Get professors for selected target team
  const targetTeam = otherTeams.find(t => 
    t.owner_id === selectedTargetUser || t.partner_id === selectedTargetUser
  );
  const targetProfessors = targetTeam 
    ? professors.filter(p => p.assigned_to === targetTeam.id)
    : [];

  // Split trades into received and sent
  const receivedTrades = trades.filter(t => t.to_user_id === userId && t.status === 'pending');
  const sentTrades = trades.filter(t => t.from_user_id === userId && t.status === 'pending');
  const completedTrades = trades.filter(t => t.status !== 'pending');

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Propose Trade Button */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Sistema Scambi</h3>
          <p className="text-sm text-muted-foreground">Proponi e gestisci scambi di professori</p>
        </div>
        <Dialog open={showProposeDialog} onOpenChange={setShowProposeDialog}>
          <DialogTrigger asChild>
            <Button disabled={!myTeam || myProfessors.length === 0}>
              <ArrowRightLeft className="mr-2 h-4 w-4" />
              Proponi Scambio
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Proponi Scambio</DialogTitle>
              <DialogDescription>
                Seleziona un tuo professore e uno di un altro team per proporre uno scambio
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* My Professor */}
              <div className="space-y-2">
                <Label>Il mio professore</Label>
                <Select value={selectedMyProfessor} onValueChange={setSelectedMyProfessor}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona professore" />
                  </SelectTrigger>
                  <SelectContent>
                    {myProfessors.map(prof => (
                      <SelectItem key={prof.id} value={prof.id}>
                        {prof.name} - {prof.subject} ({prof.total_points} punti)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Target User/Team */}
              <div className="space-y-2">
                <Label>Team avversario</Label>
                <Select 
                  value={selectedTargetUser} 
                  onValueChange={(val) => {
                    setSelectedTargetUser(val);
                    setSelectedTargetProfessor(''); // Reset professor selection
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona team" />
                  </SelectTrigger>
                  <SelectContent>
                    {otherTeams.map(team => (
                      <SelectItem key={team.id} value={team.owner_id}>
                        {team.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Target Professor */}
              {selectedTargetUser && (
                <div className="space-y-2">
                  <Label>Professore avversario</Label>
                  <Select value={selectedTargetProfessor} onValueChange={setSelectedTargetProfessor}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona professore" />
                    </SelectTrigger>
                    <SelectContent>
                      {targetProfessors.map(prof => (
                        <SelectItem key={prof.id} value={prof.id}>
                          {prof.name} - {prof.subject} ({prof.total_points} punti)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowProposeDialog(false)}>
                Annulla
              </Button>
              <Button onClick={handleProposeTrade} disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Proponi'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* No team warning */}
      {!myTeam && (
        <Card className="border-yellow-500">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-yellow-600">
              <AlertCircle className="h-5 w-5" />
              <p className="text-sm">Devi avere un team per proporre scambi</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Received Trades */}
      {receivedTrades.length > 0 && (
        <Card className="border-blue-200 dark:border-blue-800">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900">
            <CardTitle className="flex items-center gap-2">
              Scambi Ricevuti
              <Badge className="bg-red-500 hover:bg-red-600">{receivedTrades.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <ScrollArea className="h-[300px]">
              <div className="space-y-4">
                {receivedTrades.map(trade => (
                  <Card 
                    key={trade.id} 
                    onClick={() => {
                      setSelectedTrade(trade);
                      setShowTradeModal(true);
                    }}
                    className="border-2 border-blue-300 dark:border-blue-700 shadow-lg bg-gradient-to-br from-white to-blue-50 dark:from-gray-900 dark:to-blue-950 cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all"
                  >
                    <CardContent className="pt-6">
                      <div className="space-y-4">
                        {/* Header with Teams */}
                        <div className="flex items-center justify-between pb-3 border-b">
                          <div className="flex items-center gap-2">
                            <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm">
                              {trade.from_team_name.substring(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-semibold text-sm">{trade.from_team_name}</p>
                              <p className="text-xs text-muted-foreground">Propone scambio</p>
                            </div>
                          </div>
                          <Badge className="bg-yellow-500 hover:bg-yellow-600">In Attesa</Badge>
                        </div>
                        
                        {/* Players Exchange */}
                        <div className="grid grid-cols-[1fr,auto,1fr] gap-3 items-center py-2">
                          {/* From Professor */}
                          <div className="text-center p-4 bg-gradient-to-br from-red-50 to-red-100 dark:from-red-950 dark:to-red-900 rounded-lg border-2 border-red-300 dark:border-red-700">
                            <div className="flex flex-col items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center text-white text-xs font-bold">
                                {trade.from_professor_name.substring(0, 1).toUpperCase()}
                              </div>
                              <div>
                                <p className="font-bold text-sm">{trade.from_professor_name}</p>
                                <p className="text-xs text-muted-foreground">Calciatore offerto</p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Arrow */}
                          <div className="flex flex-col items-center">
                            <ArrowRightLeft className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                          </div>
                          
                          {/* To Professor */}
                          <div className="text-center p-4 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950 dark:to-green-900 rounded-lg border-2 border-green-300 dark:border-green-700">
                            <div className="flex flex-col items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white text-xs font-bold">
                                {trade.to_professor_name.substring(0, 1).toUpperCase()}
                              </div>
                              <div>
                                <p className="font-bold text-sm">{trade.to_professor_name}</p>
                                <p className="text-xs text-muted-foreground">Calciatore richiesto</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2 pt-2">
                          <Button 
                            size="sm" 
                            className="flex-1 bg-green-600 hover:bg-green-700"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAcceptTrade(trade.id);
                            }}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Accetta
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1 border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRejectTrade(trade.id);
                            }}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Rifiuta
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* Sent Trades */}
      {sentTrades.length > 0 && (
        <Card className="border-purple-200 dark:border-purple-800">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900">
            <CardTitle className="flex items-center gap-2">
              Scambi Proposti
              <Badge className="bg-purple-500 hover:bg-purple-600">{sentTrades.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <ScrollArea className="h-[300px]">
              <div className="space-y-4">
                {sentTrades.map(trade => (
                  <Card 
                    key={trade.id} 
                    onClick={() => {
                      setSelectedTrade(trade);
                      setShowTradeModal(true);
                    }}
                    className="border-2 border-purple-200 dark:border-purple-800 shadow-md cursor-pointer hover:shadow-xl hover:scale-[1.02] transition-all"
                  >
                    <CardContent className="pt-6">
                      <div className="space-y-4">
                        {/* Header */}
                        <div className="flex items-center justify-between pb-3 border-b">
                          <div className="text-sm">
                            Proposta a <span className="font-semibold">{trade.to_team_name}</span>
                          </div>
                          <Badge variant="outline" className="border-yellow-500 text-yellow-700 bg-yellow-50 dark:bg-yellow-950">
                            In attesa
                          </Badge>
                        </div>
                        
                        {/* Players Exchange */}
                        <div className="grid grid-cols-[1fr,auto,1fr] gap-3 items-center py-2">
                          {/* Your Professor */}
                          <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 rounded-lg border-2 border-blue-300 dark:border-blue-700">
                            <div className="flex flex-col items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">
                                {trade.from_professor_name.substring(0, 1).toUpperCase()}
                              </div>
                              <div>
                                <p className="font-bold text-sm">{trade.from_professor_name}</p>
                                <p className="text-xs text-muted-foreground">Tuo</p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Arrow */}
                          <div className="flex flex-col items-center">
                            <ArrowRightLeft className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                          </div>
                          
                          {/* Their Professor */}
                          <div className="text-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-lg border-2 border-gray-300 dark:border-gray-700">
                            <div className="flex flex-col items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-gray-500 flex items-center justify-center text-white text-xs font-bold">
                                {trade.to_professor_name.substring(0, 1).toUpperCase()}
                              </div>
                              <div>
                                <p className="font-bold text-sm">{trade.to_professor_name}</p>
                                <p className="text-xs text-muted-foreground">Loro</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Cancel Button */}
                        <Button 
                          size="sm" 
                          variant="destructive" 
                          className="w-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCancelTrade(trade.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Cancella Proposta
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* No pending trades */}
      {receivedTrades.length === 0 && sentTrades.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center text-muted-foreground py-8">
              <ArrowRightLeft className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Nessuno scambio pendente</p>
              <p className="text-sm mt-1">Proponi uno scambio per iniziare!</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Completed Trades History */}
      {completedTrades.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Storico Scambi</CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[200px]">
              <div className="space-y-2">
                {completedTrades.map(trade => (
                  <div key={trade.id} className="flex items-center justify-between p-3 border rounded text-sm">
                    <div className="flex items-center gap-2">
                      <span>{trade.from_professor_name}</span>
                      <ArrowRightLeft className="h-3 w-3" />
                      <span>{trade.to_professor_name}</span>
                    </div>
                    <Badge variant={trade.status === 'accepted' ? 'default' : 'secondary'}>
                      {trade.status === 'accepted' ? 'Accettato' : trade.status === 'rejected' ? 'Rifiutato' : 'Cancellato'}
                    </Badge>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
